/**
 * SiteHeader
 * ----------
 * Single source of truth for the public site header. Used on every
 * public-facing page (LandingPage, SolutionsOverviewPage,
 * AmericanSurgicalPage, WaterTreatmentPage, HowItWorksPage,
 * BackorderSourcingPage, etc).
 *
 * Features
 *  - Sticky, dawn-sky styled (matches homepage), shrinks on scroll
 *  - Dual logo lockup (Sterile Mate + AskIsaac) — clicks → home
 *  - Desktop nav: Home · How It Works · Solutions · Surgical · Marketplace · Backorders
 *  - Active route highlight (cyan)
 *  - Cross-page anchor support — "/#quick-quote" works from any page
 *  - Mobile slide-down drawer with full nav
 *  - "Home" call-to-action always one click away (logo + nav link + mobile)
 */
import { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Menu, X, Phone, ChevronDown, ShoppingCart } from "lucide-react";
import { useQuoteCart } from "@/contexts/QuoteCartContext";
import { SiteSearchButton } from "@/pages/SiteSearch";

const STERILEMATE_LOGO = "/logos/sterilemate-white.png?v=4";
const ASKISAAC_LOGO =
  "/logos/askisaac-b-monogram.svg";

// Each entry: { label, to (Link), href (anchor), match (path startsWith), badge }
const NAV_ITEMS = [
  { label: "Home", to: "/", match: "__exact__" },
  {
    label: "How It Works",
    to: "/how-it-works",
    match: "/how-it-works",
    submenu: [
      { label: "Platform Overview", to: "/how-it-works", desc: "End-to-end explainer" },
      { label: "Backorder Sourcing", to: "/how-it-works/backorder-sourcing", desc: "How we hunt allocated SKUs", badge: "For Hospitals" },
    ],
  },
  { label: "Solutions", to: "/solutions", match: "/solutions" },
  {
    label: "Marketplace",
    to: "/marketplace",
    match: "/marketplace",
    submenu: [
      { label: "All Marketplace Products", to: "/marketplace", desc: "Full catalog · every category" },
      { label: "Weekly Deals / Backorder Items", to: "/backorders/live-inventory", desc: "Live hard-to-find inventory · search any time", badge: "Live" },
      { label: "Surgical Marketplace", to: "/sterilemate/surgical", desc: "2,000+ SKUs · Aspen · BluBox" },
      { label: "Neuro Patty Sponges — American Surgical", to: "/marketplace/american-surgical", desc: "Ray-Cot® / Delicot® · Salem, MA · Authorized distributor", badge: "In Stock" },
      { label: "Neuro Patty Sponges — FABCO", to: "/marketplace/product/fabco-neuro-patties", desc: "FABCO · fabco.net · 9 SKUs · specialty OTG · RFQ", badge: "In Stock" },
      { label: "ENT — BEAR-ENT", to: "/sterilemate/ent-surgical", desc: "93+ SKUs · Ear · Nose · Throat" },
      { label: "Sol-M · Diabetes Care", to: "/sterilemate/diabetes-care", desc: "Authorized distributor" },
      { label: "Sol-M · Medication Delivery", to: "/sterilemate/medication-delivery", desc: "Syringes · needles · IV" },
      { label: "Sol-M · Blood Collection", to: "/sterilemate/blood-collection", desc: "Tubes · lancets · holders" },
      { label: "Sol-M · Wound Care", to: "/sterilemate/wound-care", desc: "Dressings · bandages" },
      { label: "Sol-M · PPE", to: "/sterilemate/ppe", desc: "Gowns · gloves · masks" },
      { label: "Disposable Gloves", to: "/marketplace/gloves", desc: "49 SKUs · Nitrile · Latex · Vinyl · Poly", badge: "New" },
      { label: "MicroCare · Sterile Processing", to: "/sterile-processing", desc: "ProEZ · enzymatic detergents · pre-treatment", badge: "New" },
      { label: "Sol-M · Vascular Access", to: "/sterilemate/vascular-access", desc: "Infusion · catheters" },
      { label: "Sol-M · Incontinence", to: "/sterilemate/incontinence", desc: "Briefs · pads · underpads" },
      { label: "Sol-M · Bulk Non-Sterile", to: "/sterilemate/bulk-non-sterile", desc: "Bulk economy line" },
      { label: "Janitorial & EVS", to: "/marketplace?category=evs-janitorial", desc: "Microfiber · Filmop · chemicals" },
      { label: "Microfiber Cleaning", to: "/marketplace?category=evs-janitorial&sub=Microfiber", desc: "70+ SKUs · Direct Mop Sales · cloths · mops · dusters · handles", badge: "New" },
      { label: "Home Health Care Aids", to: "/marketplace?category=home-health-aids", desc: "BP monitors · diabetes · daily-living aids" },
      { label: "Diabetes Management", to: "/solutions/diabetes-management", desc: "Glucose monitors · test strips · lancets · insulin syringes", badge: "New" },
      { label: "Blood Pressure Monitors", to: "/marketplace?category=blood-pressure-monitors", desc: "Automatic · aneroid · ambulatory · pediatric cuffs" },
      { label: "Filmop USA", to: "/marketplace?category=filmop-usa", desc: "Alpha trolleys · frames · poles" },
      { label: "DI Tank Exchange", to: "/marketplace?category=di-tank-exchange", desc: "Water treatment · dispatch program" },
      { label: "Hemodialysis Product & Care", to: "/dialysis", desc: "Water · concentrates · additives · equipment · compliance", badge: "New" },
      { label: "Healthcare Water Purification", to: "/marketplace?category=water-purification", desc: "Hydronix ROS-IXC · softeners · monitoring · RO/DI", badge: "ST108" },
      { label: "Medical Devices", to: "/marketplace/medical-devices", desc: "BP Monitors · Blood Collection · Needles & Syringes", badge: "New" },
      { label: "— Power Mobility", to: "/marketplace/medical-devices/vive-power-mobility", desc: "Scooters · power wheelchairs · batteries", indent: true },
      { label: "— Mobility", to: "/marketplace/medical-devices/vive-mobility", desc: "Rollators · walkers · canes · reachers", indent: true },
      { label: "— Bracing", to: "/marketplace/medical-devices/vive-bracing", desc: "Knee · back · ankle · wrist · shoulder", indent: true },
      { label: "— Health Devices", to: "/marketplace/medical-devices/vive-health-devices", desc: "TENS · pulse-ox · thermometers · scales", indent: true },
      { label: "— Bathroom", to: "/marketplace/medical-devices/vive-bathroom", desc: "Grab bars · toilet rails · transfer benches", indent: true },
      { label: "— Bedroom", to: "/marketplace/medical-devices/vive-bedroom", desc: "Bed rails · wedges · overbed tables", indent: true },
      { label: "— Living Aids", to: "/marketplace/medical-devices/vive-living-aids", desc: "Reachers · dressing sticks · sock aids", indent: true },
      { label: "— Footcare", to: "/marketplace/medical-devices/vive-footcare", desc: "Plantar relief · arch supports · toe separators", indent: true },
      { label: "— Physical Therapy", to: "/marketplace/medical-devices/vive-physical-therapy", desc: "TENS · foam rollers · resistance bands", indent: true },
      { label: "Green Klean®", to: "/marketplace/green-klean", desc: "EPA #71847-6-91038", badge: "New" },
      { label: "Microbiology Testing", to: "/microbiology-testing", desc: "A&B Labs · ISO 17025 · Water + bio-burden panels" },
    ],
  },
];

export default function SiteHeader() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [openSubmenu, setOpenSubmenu] = useState(null);
  const location = useLocation();
  const navigate = useNavigate();
  const { cart, setShowCart } = useQuoteCart();
  const cartCount = cart.reduce((s, i) => s + (i.qty || 0), 0);

  // "Backorders" tab → always lands on the homepage Quick Quote form. React
  // Router doesn't natively follow hash anchors after a route change, so we
  // navigate first and then poll for the target element to scroll into view.
  // Works from ANY page (BackorderSourcing landing page, How It Works, etc.).
  const goToBackorders = () => {
    setMobileOpen(false);
    const scrollToTarget = () => {
      let attempts = 0;
      const tick = () => {
        attempts += 1;
        const el = document.getElementById("quick-quote");
        if (el) {
          el.scrollIntoView({ behavior: "smooth", block: "start" });
          return;
        }
        if (attempts < 30) setTimeout(tick, 100);
      };
      setTimeout(tick, 100);
    };
    if (location.pathname === "/") {
      // Already on home — just scroll.
      scrollToTarget();
    } else {
      navigate("/");
      scrollToTarget();
    }
  };

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 16);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  // Close mobile drawer on navigation
  useEffect(() => { setMobileOpen(false); setOpenSubmenu(null); }, [location.pathname]);

  const isActive = (item) => {
    if (item.match === "__exact__") return location.pathname === "/";
    return location.pathname.startsWith(item.match);
  };

  return (
    <>
      <header
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
          scrolled ? "shadow-lg shadow-black/30" : ""
        }`}
        data-testid="site-header"
        style={{
          background: scrolled
            ? "linear-gradient(90deg, #050a16 0%, #0a1a35 30%, #0f2a5a 60%, #0056A8 100%)"
            : "linear-gradient(90deg, #050a16 0%, #0b1e3f 32%, #123a75 65%, #0056A8 100%)",
          borderBottom: scrolled ? "1px solid rgba(255,255,255,0.08)" : "none",
        }}
      >
        {/* Left dark fade — keeps logos crisp */}
        <div
          aria-hidden
          className="absolute inset-y-0 left-0 pointer-events-none w-[440px]"
          style={{
            background:
              "linear-gradient(90deg, rgba(5,10,22,0.85) 0%, rgba(5,10,22,0.55) 45%, rgba(5,10,22,0) 100%)",
          }}
        />
        <div className={`relative max-w-7xl mx-auto px-4 md:px-6 flex items-center justify-between gap-3 md:gap-6 transition-all duration-300 ${scrolled ? "h-14 md:h-16" : "h-16 md:h-24"}`}>
          {/* Logo lockup — links home */}
          <Link to="/" className="flex items-center gap-2 md:gap-3 shrink-0 min-w-0 relative" data-testid="site-header-logo">
            <img
              src={STERILEMATE_LOGO}
              alt="Sterile Mate"
              className={`w-auto shrink-0 object-contain transition-all duration-300 ${scrolled ? "h-9 md:h-12" : "h-12 md:h-16"}`}
            />
          </Link>

          {/* Desktop nav */}
          <nav className="hidden lg:flex items-center gap-5 xl:gap-7" data-testid="site-header-nav">
            {NAV_ITEMS.map((item) => {
              const active = isActive(item);
              const linkClass = `text-sm font-medium transition-colors whitespace-nowrap flex items-center gap-1 ${
                active ? "text-cyan-300" : "text-white/85 hover:text-white"
              }`;
              if (!item.submenu) {
                return (
                  <Link key={item.label} to={item.to} className={linkClass} data-testid={`site-header-${item.label.toLowerCase().replace(/\s+/g, "-")}`}>
                    {item.label}
                  </Link>
                );
              }
              return (
                <div key={item.label} className="relative group">
                  <Link to={item.to} className={linkClass}>
                    {item.label}
                    <ChevronDown className="w-3 h-3 mt-0.5" />
                  </Link>
                  <div className="absolute left-0 top-full pt-2 w-[360px] opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all z-50">
                    <div className="bg-[#0b1d3a] border border-slate-700 rounded-xl shadow-2xl overflow-hidden max-h-[80vh] overflow-y-auto">
                      {item.submenu.map((sub) => (
                        <Link
                          key={sub.to}
                          to={sub.to}
                          className={`block px-4 py-3 hover:bg-slate-800/60 border-b border-slate-800 last:border-b-0 ${sub.indent ? 'pl-8 bg-slate-900/40' : ''}`}
                        >
                          <div className="flex items-center gap-2">
                            <p className={`${sub.indent ? 'text-xs' : 'text-sm'} font-semibold text-white`}>{sub.label}</p>
                            {sub.badge && (
                              <span className="px-1.5 py-0.5 text-[9px] font-black uppercase tracking-wider rounded bg-rose-500/20 text-rose-300 border border-rose-500/30">
                                {sub.badge}
                              </span>
                            )}
                          </div>
                          {sub.desc && <p className="text-xs text-slate-400 mt-0.5">{sub.desc}</p>}
                        </Link>
                      ))}
                    </div>
                  </div>
                </div>
              );
            })}
            {/* Backorders CTA — anchors to homepage backorder-submission form */}
            <button
              type="button"
              onClick={goToBackorders}
              className="text-sm font-semibold text-amber-400 hover:text-amber-300 transition-colors whitespace-nowrap"
              data-testid="site-header-quick-quote"
            >
              Backorders
            </button>
          </nav>

          {/* Right cluster */}
          <div className="flex items-center gap-3 shrink-0">
            {/* Global site search — magnifier + Cmd+K modal */}
            <div className="hidden md:block">
              <SiteSearchButton />
            </div>
            <div className="md:hidden">
              <SiteSearchButton compact />
            </div>
            <a href="tel:2814563458" className="hidden 2xl:inline-flex text-sm font-medium text-white/70 hover:text-white transition-colors items-center gap-1 whitespace-nowrap">
              <Phone className="w-3 h-3" /> 281.456.3458
            </a>
            <Link to={`/hospital/login?next=${encodeURIComponent(location.pathname + location.search)}`} className="hidden xl:inline-block text-sm font-medium text-white/80 hover:text-white transition-colors whitespace-nowrap" data-testid="site-header-hospital-login">
              Hospital Login
            </Link>
            {/* Quote Cart button — visible on every page */}
            <button
              type="button"
              onClick={() => setShowCart(true)}
              className="relative inline-flex items-center gap-1.5 px-3 h-9 rounded-lg bg-white/10 hover:bg-white/15 border border-white/20 text-white text-sm font-semibold whitespace-nowrap transition-colors backdrop-blur-sm"
              data-testid="site-header-cart"
              aria-label={`Open quote cart (${cartCount} items)`}
            >
              <ShoppingCart className="w-4 h-4 text-amber-400" />
              <span className="hidden sm:inline">Cart</span>
              {cartCount > 0 && (
                <span className="inline-flex items-center justify-center min-w-[20px] h-[20px] rounded-full bg-amber-500 text-slate-900 text-[10px] font-black px-1.5" data-testid="site-header-cart-count">
                  {cartCount}
                </span>
              )}
            </button>
            <Link
              to="/"
              className="hidden lg:inline-flex items-center gap-1.5 px-4 h-9 rounded-lg bg-amber-500 hover:bg-amber-400 text-slate-900 text-sm font-bold shadow-md shadow-amber-500/25 whitespace-nowrap transition-colors"
              data-testid="site-header-home-cta"
            >
              ← Home
            </Link>
            <button
              onClick={() => setMobileOpen(true)}
              aria-label="Open menu"
              className="lg:hidden w-10 h-10 inline-flex items-center justify-center rounded-md text-white/90 hover:text-white hover:bg-white/10 transition-colors"
              data-testid="site-header-mobile-toggle"
            >
              <Menu className="w-6 h-6" />
            </button>
          </div>
        </div>
      </header>

      {/* Spacer so page content doesn't slide under the fixed header */}
      <div aria-hidden className={`transition-all duration-300 ${scrolled ? "h-14 md:h-16" : "h-16 md:h-24"}`} />

      {/* Mobile drawer */}
      {mobileOpen && (
        <div className="lg:hidden fixed inset-0 z-[60]" data-testid="site-header-mobile-drawer">
          <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={() => setMobileOpen(false)} />
          <div className="absolute top-0 inset-x-0 bg-[#0a0f1a] border-b border-slate-700 shadow-2xl">
            <div className="flex items-center justify-between px-5 h-14 border-b border-slate-800">
              <Link to="/" className="flex items-center gap-2" onClick={() => setMobileOpen(false)}>
                <img src={STERILEMATE_LOGO} alt="Sterile Mate" className="h-6 w-auto" />
                <div className="h-4 w-px bg-slate-700" />
                <img src={ASKISAAC_LOGO} alt="AskIsaac" className="h-5 w-auto" />
              </Link>
              <button
                onClick={() => setMobileOpen(false)}
                aria-label="Close menu"
                className="w-9 h-9 inline-flex items-center justify-center rounded-md text-slate-300 hover:text-white hover:bg-white/5"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            <nav className="px-3 py-3 max-h-[80vh] overflow-y-auto">
              {NAV_ITEMS.map((item) => (
                <div key={item.label}>
                  {!item.submenu ? (
                    <Link
                      to={item.to}
                      onClick={() => setMobileOpen(false)}
                      className={`block px-3 py-2.5 rounded text-sm font-semibold ${
                        isActive(item) ? "bg-cyan-500/10 text-cyan-200" : "text-slate-200 hover:bg-slate-800/60"
                      }`}
                    >
                      {item.label}
                    </Link>
                  ) : (
                    <>
                      <button
                        type="button"
                        onClick={() => setOpenSubmenu(openSubmenu === item.label ? null : item.label)}
                        className={`w-full flex items-center justify-between px-3 py-2.5 rounded text-sm font-semibold ${
                          isActive(item) ? "bg-cyan-500/10 text-cyan-200" : "text-slate-200 hover:bg-slate-800/60"
                        }`}
                      >
                        <span>{item.label}</span>
                        <ChevronDown className={`w-4 h-4 transition-transform ${openSubmenu === item.label ? "rotate-180" : ""}`} />
                      </button>
                      {openSubmenu === item.label && (
                        <div className="pl-4 my-1 space-y-1">
                          {item.submenu.map((sub) => (
                            <Link
                              key={sub.to}
                              to={sub.to}
                              onClick={() => setMobileOpen(false)}
                              className="block px-3 py-2 rounded text-sm text-slate-300 hover:bg-slate-800/60 hover:text-white"
                            >
                              <div className="flex items-center gap-2">
                                {sub.label}
                                {sub.badge && (
                                  <span className="px-1.5 py-0.5 text-[9px] font-black uppercase tracking-wider rounded bg-rose-500/20 text-rose-300 border border-rose-500/30">
                                    {sub.badge}
                                  </span>
                                )}
                              </div>
                            </Link>
                          ))}
                        </div>
                      )}
                    </>
                  )}
                </div>
              ))}
              <button
                type="button"
                onClick={goToBackorders}
                className="block w-full text-left mt-2 px-3 py-2.5 rounded text-sm font-bold text-amber-300 bg-amber-500/10 hover:bg-amber-500/20"
                data-testid="site-header-mobile-quick-quote"
              >
                Backorders
              </button>
              <button
                type="button"
                onClick={() => { setMobileOpen(false); setShowCart(true); }}
                className="w-full mt-2 px-3 py-2.5 rounded text-sm font-bold text-amber-300 bg-amber-500/10 hover:bg-amber-500/20 flex items-center justify-between"
                data-testid="site-header-mobile-cart"
              >
                <span className="flex items-center gap-2"><ShoppingCart className="w-4 h-4" /> Quote Cart</span>
                {cartCount > 0 && (
                  <span className="inline-flex items-center justify-center min-w-[20px] h-[20px] rounded-full bg-amber-500 text-slate-900 text-[10px] font-black px-1.5">
                    {cartCount}
                  </span>
                )}
              </button>
              <div className="mt-3 pt-3 border-t border-slate-800 grid grid-cols-2 gap-2">
                <Link to={`/hospital/login?next=${encodeURIComponent(location.pathname + location.search)}`} onClick={() => setMobileOpen(false)} className="px-3 py-2 rounded text-xs text-center text-slate-300 bg-slate-900/70 border border-slate-700/60 hover:bg-slate-800/60">
                  Hospital Login
                </Link>
                <Link to="/login" onClick={() => setMobileOpen(false)} className="px-3 py-2 rounded text-xs text-center text-slate-300 bg-slate-900/70 border border-slate-700/60 hover:bg-slate-800/60">
                  Staff Login
                </Link>
              </div>
              <a href="tel:2814563458" className="mt-2 w-full flex items-center justify-center gap-1.5 px-3 py-2 rounded text-xs text-slate-400 hover:text-white">
                <Phone className="w-3 h-3" /> 281.456.3458
              </a>
            </nav>
          </div>
        </div>
      )}
    </>
  );
}
